//@flow
import * as Immutable from 'immutable';

import EntityTypeEnum from 'common/enums/EntityTypeEnum';

import { MasterVehicle } from 'common/models/flow/MasterVehicle';
import type { MasterVehicleModelType } from 'common/models/flow/MasterVehicle';

import { TeamId } from './TeamId';
import { SkillId } from './SkillId';
import { VehicleCostModel } from './VehicleCostModel';
import { RiderId } from './RiderId';
import { ResourceLimit } from './ResourceLimit';
import { AuditMetadata } from './AuditMetadata';

import {
    validatable,
    validate,
    validateAll,
    validateId,
    softValidateAll
} from 'common/utils/ValidationUtil';

import EntityImportDataHelper from 'common/entity/helpers/EntityImportDataHelper';

import DataUtils from 'common/utils/DataUtils';
import NumberUtils from 'common/utils/NumberUtils';
import TimeUtils from 'common/utils/TimeUtils';
import RegExUtil from 'common/utils/RegExUtil';

import I18nHelper from 'common/helpers/I18nHelper';

import { Shift } from './Shift';

import { Document } from './Document';

import { MasterVehicleModel } from './MasterVehicleModel';
import { VehicleModelId } from './VehicleModelId';

import { BaseLocations } from './BaseLocations';

import { LDate } from 'common/models/internal/LDate';

import { MasterRider } from './MasterRider';

const translationsNamespace = ['entityManagement', 'validations'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

const ENTITY_NAME_ID = 'masterVehicle';

export const FuelTypeType = new EntityTypeEnum({
    PETROL: 'PETROL',
    DIESEL: 'DIESEL'
});

const validateNumber = (value: any) => {
    if (!DataUtils.isEmpty(value) && !NumberUtils.isNumeric(value)) {
        return nsTranslate('validations:vehicleValidationErrors:notANumber');
    }
};

const validateBoolean = (value: any, isRequired: boolean) => {
    if (
        (!DataUtils.isEmpty(value) || isRequired) &&
        !DataUtils.isBoolean(value)
    ) {
        return nsTranslate(
            'validations:vehicleValidationErrors.booleanValueValidation',
            {
                positiveLabel: nsTranslate(
                    'entityManagement:masterVehicleProperties.boolean.yes'
                ),
                negativeLabel: nsTranslate(
                    'entityManagement:masterVehicleProperties.boolean.no'
                )
            }
        );
    }
};

@validatable
class InternalMasterVehicle extends MasterVehicle {
    static fromJS(json: MasterVehicleModelType): InternalMasterVehicle {
        const state: Object = Object.assign({}, json);
        state.riderId = state.riderId
            ? RiderId.fromJS(state.riderId)
            : state.riderId;
        state.rider = state.rider
            ? MasterRider.fromJS(state.rider)
            : state.rider;
        state.vehicleCostModel = VehicleCostModel.fromJS(
            state.vehicleCostModel
        );
        state.teams = Immutable.List(state.teams).map(item =>
            TeamId.fromJS(item)
        );
        state.skills = Immutable.List(state.skills).map(item =>
            SkillId.fromJS(item)
        );
        state.shifts = Immutable.List(state.shifts).map(item =>
            Shift.fromJS(item)
        );
        state.resourceLimits = Immutable.List(state.resourceLimits).map(item =>
            ResourceLimit.fromJS(item)
        );
        state.manufacturingDate = state.manufacturingDate
            ? LDate.fromJS(state.manufacturingDate)
            : state.manufacturingDate;
        state.documents = Immutable.List(state.documents).map(item =>
            Document.fromJS(item)
        );
        state.customProperties = Immutable.Map(state.customProperties);
        state.auditMetadata = state.auditMetadata
            ? AuditMetadata.fromJS(state.auditMetadata)
            : state.auditMetadata;
        state.vehicleModel = state.vehicleModel
            ? MasterVehicleModel.fromJS(state.vehicleModel)
            : state.vehicleModel;
        state.vehicleModelId = state.vehicleModelId
            ? VehicleModelId.fromJS(state.vehicleModelId)
            : state.vehicleModelId;
        state.baseLocations = state.baseLocations
            ? BaseLocations.fromJS(state.baseLocations)
            : state.baseLocations;
        return new this(Immutable.Map(state));
    }

    @validate
    setId(id: string): this {
        return super.setId(id);
    }

    validateId() {
        return validateId(this.id);
    }

    @validate
    setCode(code: string): this {
        return super
            .setCode(code)
            .setId(!DataUtils.isEmpty(code) ? RegExUtil.conformToId(code) : '');
    }

    validateCode() {
        const code = this.code || this.id;
        if (DataUtils.isEmpty(code)) {
            return nsTranslate('entityValidationErrors.codeEmpty');
        }
        if (code.trim().length < 2) {
            return nsTranslate('entityValidationErrors.codeLessThan2');
        }
        if (code.trim().length > 32) {
            return nsTranslate('entityValidationErrors.codeGreaterThan32');
        }
    }

    @validate
    setName(name: string): this {
        return super.setName(name);
    }

    validateName() {
        if (
            DataUtils.isEmpty(this.name) ||
            DataUtils.isEmpty(this.name.trim())
        ) {
            return nsTranslate('entityValidationErrors.nameEmpty', {
                entityNameId: ENTITY_NAME_ID
            });
        }
    }

    @validate
    setTeams(teams: Immutable.List<TeamId>): this {
        return super.setTeams(teams);
    }

    validateTeams() {
        if (DataUtils.isEmpty(this.teams) || this.teams.size <= 0) {
            return nsTranslate('vehicleValidationErrors.teamEmpty');
        }
    }

    @validate
    setManufacturingDate(manufacturingDate: ?string): this {
        return super.setManufacturingDate(manufacturingDate);
    }

    validateManufacturingDate() {
        if (
            !DataUtils.isEmpty(this.manufacturingDate) &&
            !TimeUtils.format(this.manufacturingDate)
        ) {
            return nsTranslate(
                'vehicleValidationErrors.invalidManufacturingDate'
            );
        }
    }

    @validate
    setMileage(mileage: number): this {
        return super.setMileage(mileage);
    }

    validateMileage() {
        return validateNumber(this.mileage);
    }

    @validate
    setVehicleModelId(vehicleModelId: VehicleModelId): this {
        return super.setVehicleModelId(vehicleModelId);
    }

    validateVehicleModelId() {
        if (DataUtils.isEmpty(this.vehicleModelId)) {
            return nsTranslate('vehicleValidationErrors.vehicleModelEmpty');
        }
    }

    @validate
    setRegistrationNumber(registrationNumber: ?string): this {
        return super.setRegistrationNumber(registrationNumber);
    }

    validateRegistrationNumber() {
        if (DataUtils.isEmpty(this.registrationNumber)) {
            return nsTranslate(
                'vehicleValidationErrors.registrationNumberEmpty'
            );
        }
    }

    @validate
    setIsActive(isActive: boolean): this {
        return super.setIsActive(isActive);
    }

    validateIsActive(): ?string {
        return validateBoolean(this.isActive, true);
    }

    setHiddenModelProperties(hiddenModelProperties: Immutable.List<string>) {
        return this.clone(
            this._state.set('hiddenModelProperties', hiddenModelProperties)
        );
    }

    getHiddenModelProperties(): Immutable.List<string> {
        return this._state.get('hiddenModelProperties');
    }

    isHidden(key: string) {
        const hiddenModelProperties = this.getHiddenModelProperties();
        const [groupKey] = key.split('.');
        return (
            (hiddenModelProperties && hiddenModelProperties.includes(key)) ||
            (hiddenModelProperties && hiddenModelProperties.includes(groupKey))
        );
    }

    setDocuments(documents: Immutable.List<Document>): this {
        return super.setDocuments(documents);
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        return this.documents.some(currentDocument =>
            currentDocument.hasValidationErrors()
        );
    }

    @validateAll
    setAllValidationErrors(): this {
        let { documents } = this;
        if (documents) {
            documents = documents.map(doc => doc.setAllValidationErrors());
        }
        return this.setDocuments(documents);
    }

    getCellValidationMessage({ header, validationData }) {
        return EntityImportDataHelper.getCellValidationMessage({
            entity: this,
            header,
            validationData
        });
    }

    sanitize(): this {
        const {
            baseLocations: { startLocation, endLocation }
        } = this;
        let sanitizedVehicle = this;
        if (
            DataUtils.isEmpty(startLocation) &&
            DataUtils.isEmpty(endLocation)
        ) {
            sanitizedVehicle = sanitizedVehicle.setBaseLocations(null);
        }
        return sanitizedVehicle;
    }

    // Alternative to lazy loading of nested models (init and cleaning)
    defaultSanitize(): ?this {
        const sanitizedVehicle = super.defaultSanitize();
        if (sanitizedVehicle) {
            const sanitizedDocuments = sanitizedVehicle.documents
                .map(doc => doc.defaultSanitize())
                .filter(doc => !DataUtils.isEmpty(doc.imageUrl));
            return sanitizedVehicle
                .setDocuments(sanitizedDocuments)
                .setVehicleModel(null);
        }
        return sanitizedVehicle;
    }

    static defaultInit(): InternalMasterVehicle {
        return InternalMasterVehicle.fromJS({
            isActive: true,
            baseLocations: BaseLocations.fromJS({})
        });
    }

    get riderId(): ?RiderId {
        return this._state.get('riderId');
    }

    get vehicleCostModel(): VehicleCostModel {
        return this._state.get('vehicleCostModel');
    }

    get teams(): Immutable.List<TeamId> {
        return this._state.get('teams');
    }

    get skills(): Immutable.List<SkillId> {
        return this._state.get('skills');
    }

    get resourceLimits(): Immutable.List<ResourceLimit> {
        return this._state.get('resourceLimits');
    }

    get auditMetadata(): ?AuditMetadata {
        return this._state.get('auditMetadata');
    }

    get shifts(): Immutable.List<Shift> {
        return this._state.get('shifts');
    }

    get documents(): Immutable.List<Document> {
        return this._state.get('documents');
    }

    get vehicleModel(): ?MasterVehicleModel {
        return this._state.get('vehicleModel');
    }

    get vehicleModelId(): ?VehicleModelId {
        return this._state.get('vehicleModelId');
    }

    get baseLocations(): ?BaseLocations {
        return this._state.get('baseLocations');
    }

    get manufacturingDate(): ?LDate {
        return this._state.get('manufacturingDate');
    }

    get rider(): ?MasterRider {
        return this._state.get('rider');
    }
}

export { InternalMasterVehicle as MasterVehicle };
